import { urlMake } from '@javascript/instant_search/carwash/makes';
import { encodeModel } from '@javascript/instant_search/carwash/models';

export const makeModelFormatter = (relatedRefinementList?: {
  [x: string]: string[];
}) => {
  if (!relatedRefinementList) return undefined;

  const [makes, models, variants] = Object.values(relatedRefinementList);

  const makeModel = makes.reduce((acc, make) => {
    acc[urlMake(make)] = ['_all'];
    return acc;
  }, {} as Record<string, string[]>);

  if (models.length === 0) return makeModel;

  models.forEach((modelLabel) => {
    const [unformattedMake, unformattedModel] = modelLabel.split(' > ');
    const make = urlMake(unformattedMake);
    const model = encodeModel(unformattedModel);
    makeModel[make][0] === '_all'
      ? (makeModel[make][0] = model)
      : makeModel[make].push(model);
  });

  if (variants.length === 0) return makeModel;

  type WithVariants = Record<string, (string | Record<string, string[]>)[]>;

  const makeModelWithVariants = makeModel as WithVariants;

  variants.forEach((variantLabel) => {
    const [unformattedMake, unformattedModel, variant] =
      variantLabel.split(' > ');
    const make = urlMake(unformattedMake);
    const model = encodeModel(unformattedModel);

    //remove the model from the makeModel object
    const modelIndex = makeModelWithVariants[make].indexOf(model);

    if (modelIndex !== -1) {
      makeModelWithVariants[make].splice(modelIndex, 1);
    }

    //add the model to the makeModel object with the variant
    makeModelWithVariants[make].push({ [model]: [variant] });
  });

  return makeModel;
};

export const paymentTypeFormatter = (
  indexUiState: Partial<{ range: { [attribute: string]: string } }>
) => {
  if (indexUiState.range?.cashPrice) {
    return 'cash';
  } else if (indexUiState.range?.monthlyPayment) {
    return 'monthly';
  }
  return undefined;
};

export const priceFormatter = (
  priceRange: string | undefined,
  minOrMax: 'min' | 'max'
) => {
  if (!priceRange) return undefined;
  const value = priceRange.split(':')[minOrMax === 'min' ? 0 : 1];
  return !['0', '', undefined].includes(value) ? value : undefined;
};

export const engineSizeFormatter = (
  engineSize: string | undefined,
  minOrMax: 'min' | 'max'
) => {
  if (!engineSize) return undefined;
  // Exclude certain values that are not valid for engine size, 7000 is used as a placeholder for no maximum & 0 is used for no minimum to skirt around Algoila's useRange limitations.
  const exclusionList = ['0', '', '7000', undefined];
  const value = engineSize.split(':')[minOrMax === 'min' ? 0 : 1];
  return !exclusionList.includes(value) ? value : undefined;
};

export const singleSelectNumericFormatter = (numericMenuValue?: string) => {
  if (numericMenuValue === undefined) return numericMenuValue;
  return numericMenuValue.replace(/:/g, '');
};

export const searchTypeFormatter = (searchType?: string) => {
  if (!searchType) return;
  if (['Value Range', 'Motorbikes', 'Vans'].includes(searchType))
    return searchType.replace(/\s/g, '+');
  //bolt "+Cars" onto the end of the searchType to match classic SERPS parsm
  return `${searchType.replace(/\s/g, '+') + '+Cars'}`;
};

const sortOrderMap: Record<string, string> = {
  monthly_asc: 'monthly_payment_up',
  monthly_dsc: 'monthly_payment_down',
  cash_asc: 'price_up',
  cash_dsc: 'price_down',
  mileage: 'mileage',
  age: 'age',
  recentlyadded: 'recently_added',
  geolocation: 'distance',
};

export const sortOrderFormatter = (
  replicaName: string | undefined,
  indexName: string
) => {
  if (!replicaName) return undefined;
  //remove the indexName from the replica name
  const sortOrder = replicaName.replace(`${indexName}_`, '');
  return sortOrderMap[sortOrder] || sortOrder;
};

export const registeredAtFormatter = (
  registeredAt?: string | null
): string | undefined => {
  if (!registeredAt) return undefined;
  const formattedRegisteredAt = registeredAt.slice(0, -1);
  const then = new Date(Number(formattedRegisteredAt) * 1000);
  const now = new Date();

  if (isNaN(then.getTime())) return undefined;

  let months =
    (now.getFullYear() - then.getFullYear()) * 12 +
    (now.getMonth() - then.getMonth());

  if (now.getDate() < then.getDate()) {
    months -= 1;
  }

  return String(months);
};
